(function(root){
'use strict';
// One venue-timezone scheduler shared by discovery, directory cards and exports.
const VERSION='2026-10-12.2';
if(root.MintyEventCore?.version===VERSION){if(typeof module==='object')module.exports=root.MintyEventCore;return;}
const DAYS=['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const CODES=['SU','MO','TU','WE','TH','FR','SA'];
const DAY=86400000;
const allowed=new Set(['','ready','published','active','verified']);
const norm=v=>String(v||'').normalize('NFKC').trim().replace(/\s+/g,' ').toLowerCase();
const zip=v=>(String(v||'').match(/^\s*(\d{5})/)||[])[1]||'';
const pad=v=>String(v).padStart(2,'0');
const dateKey=d=>d.toISOString().slice(0,10);
const fmtCache=new Map();
function zone(a){const z=a.timezone||'America/Los_Angeles';try{new Intl.DateTimeFormat('en-US',{timeZone:z});return z;}catch{return null;}}
function wall(date,tz='America/Los_Angeles'){
 let f=fmtCache.get(tz);if(!f){f=new Intl.DateTimeFormat('en-US',{timeZone:tz,year:'numeric',month:'numeric',day:'numeric',hour:'numeric',minute:'numeric',second:'numeric',hourCycle:'h23'});fmtCache.set(tz,f);}
 const p=Object.fromEntries(f.formatToParts(date).map(x=>[x.type,x.value]));
 return new Date(Date.UTC(+p.year,+p.month-1,+p.day,+p.hour,+p.minute,+p.second));
}
function validDate(s){if(!/^\d{4}-\d{2}-\d{2}$/.test(s||''))return false;const d=new Date(s+'T00:00:00Z');return Number.isFinite(+d)&&dateKey(d)===s;}
function validTime(s){return /^([01]?\d|2[0-3]):[0-5]\d$/.test(s||'');}
function shift(day,n){return dateKey(new Date(+new Date(day+'T00:00:00Z')+n*DAY));}
// A conversion boundary is not a published event time. Reject DST gaps and
// ambiguous local times rather than guessing which instant an organizer meant.
function instant(day,time,tz){
 if(!validDate(day)||!validTime(time)||!tz)return null;
 const [h,m]=time.split(':').map(Number);const target=+new Date(day+'T00:00:00Z')+h*3600000+m*60000;
 let guess=target;
 for(let i=0;i<5;i++){const delta=target-(+wall(new Date(guess),tz));if(!delta)break;guess+=delta;}
 if(+wall(new Date(guess),tz)!==target)return null;
 if([guess-3600000,guess+3600000].some(t=>+wall(new Date(t),tz)===target))return null;
 return new Date(guess);
}
function permitted(a){return allowed.has(norm(a.publication_status))&&!['true','yes','1'].includes(norm(a.publication_hold))&&!['true','yes','1'].includes(norm(a.do_not_publish));}
function ordinals(value){const words={first:1,second:2,third:3,fourth:4,fifth:5,last:-1};const parts=norm(value).match(/\b(?:[1-5](?:st|nd|rd|th)?|first|second|third|fourth|fifth|last)\b/g)||[];return [...new Set(parts.map(x=>words[x]||parseInt(x,10)))].sort((a,b)=>a-b);}
function monthlyMatch(day,ord){const d=new Date(day+'T00:00:00Z');const n=Math.floor((d.getUTCDate()-1)/7)+1;return ord.includes(n)||(ord.includes(-1)&&new Date(+d+7*DAY).getUTCMonth()!==d.getUTCMonth());}
function flatten(index){
 const byId=new Map(),blocked=new Set();
 for(const b of Object.values(index||{})){
  if(!b||b.visible===false)continue;
  for(const a of Array.isArray(b.appearances)?b.appearances:[]){
   if(!a||!a.handle)continue;
   if(!permitted(a)){blocked.add(a.handle);continue;}
   let event=byId.get(a.handle);
   if(!event){event={...a,hosts:[],enclosingBrandHandles:[]};byId.set(a.handle,event);}
   if(b.handle&&!event.enclosingBrandHandles.includes(b.handle))event.enclosingBrandHandles.push(b.handle);
   // Only explicit canonical relationships identify hosts. An enclosing
   // venue entry is never evidence that the venue is the performer.
   for(const h of Array.isArray(a.hosts)?a.hosts:[]){
    if(h&&h.handle&&!event.hosts.some(x=>x.handle===h.handle))event.hosts.push({handle:h.handle,name:h.name||h.handle,role:h.role||'host_or_performer'});
   }
  }
 }
 return [...byId.values()].filter(a=>!blocked.has(a.handle)).map(a=>{
  a.hosts.sort((x,y)=>x.handle.localeCompare(y.handle));a.enclosingBrandHandles.sort();
  a.brandName=a.hosts.map(h=>h.name).join(', ');a.brandHandle=a.hosts[0]?.handle||'';return a;
 }).sort((a,b)=>a.handle.localeCompare(b.handle));
}
function durationMs(a){const d=Number(a.duration_hours);return Number.isFinite(d)&&d>0&&d<=48?d*3600000:null;}
function finish(a,start,oneoff){
 const explicit=oneoff&&a.end_date_time?new Date(a.end_date_time):null;
 if(explicit&&Number.isFinite(+explicit)&&+explicit>+start)return explicit;
 const d=durationMs(a);return d===null?null:new Date(+start+d);
}
function occurrence(a,at=new Date(),weekday=''){
 if(!permitted(a))return null;
 const tz=zone(a);if(!tz||!Number.isFinite(+at))return {unknown:true,reason:'timezone'};
 const today=dateKey(wall(at,tz));const recur=norm(a.recurrence);
 if(!recur||recur==='none'){
  if(a.time_precision==='date_only'||(!a.date_time&&validDate(a.event_date))){
   const day=a.event_date;if(!validDate(day))return {unknown:true,reason:'date'};
   if(day<today||(weekday&&DAYS[new Date(day+'T00:00:00Z').getUTCDay()]!==weekday))return null;
   return {day,dateOnly:true,unknown:false,start:null,end:null,timezone:tz};
  }
  if(!a.date_time)return {unknown:true,reason:'date'};
  const start=new Date(a.date_time);if(!Number.isFinite(+start))return {unknown:true,reason:'date'};
  const day=dateKey(wall(start,tz));const end=finish(a,start,true);
  if(end?+end<=+at:day<today)return null;
  if(weekday&&DAYS[wall(start,tz).getUTCDay()]!==weekday)return null;
  return {day,start,end,unknown:false,timezone:tz};
 }
 if(!['weekly','monthly'].includes(recur))return {unknown:true,reason:'recurrence'};
 const days=(a.days||a.days_of_week||[]).filter(d=>DAYS.includes(d));
 if(!days.length)return {unknown:true,reason:'weekday'};
 if(weekday&&!days.includes(weekday))return null;
 const lo=validDate(a.residency_start_date)?a.residency_start_date:null;
 const hi=validDate(a.residency_end_date)?a.residency_end_date:null;
 if(hi&&hi<today)return null;
 if(lo&&hi&&lo>hi)return {unknown:true,reason:'bounds'};
 const ord=ordinals(a.monthly_occurrence);
 if(recur==='monthly'&&!ord.length)return {unknown:true,reason:'monthly_rule'};
 let anchor=shift(today,-2);if(lo&&lo>anchor)anchor=lo;
 for(let i=0;i<400;i++){
  const day=shift(anchor,i);if(hi&&day>hi)return null;
  const dn=DAYS[new Date(day+'T00:00:00Z').getUTCDay()];
  if(!days.includes(dn)||(weekday&&dn!==weekday)||(recur==='monthly'&&!monthlyMatch(day,ord)))continue;
  if(!validTime(a.start_time)){
   if(day<today)continue;
   return {day,dateOnly:true,unknown:false,start:null,end:null,timezone:tz,reason:'time'};
  }
  const start=instant(day,a.start_time,tz);
  if(!start){if(day<today)continue;return {day,dateOnly:true,timeUncertain:true,unknown:false,start:null,end:null,timezone:tz,reason:'dst'};}
  const end=finish(a,start,false);
  if(end?+end<=+at:day<today)continue;
  return {day,start,end,unknown:false,timezone:tz};
 }
 return {unknown:true,reason:'range'};
}
function isPast(a,at=new Date()){return permitted(a)&&occurrence(a,at)===null;}
function pastOccurrence(a){
 const tz=zone(a)||'America/Los_Angeles';
 if(validDate(a.event_date)&&!a.date_time)return {day:a.event_date,dateOnly:true,start:null,end:null,timezone:tz};
 const start=a.date_time?new Date(a.date_time):null;
 if(start&&Number.isFinite(+start))return {day:dateKey(wall(start,tz)),start,end:finish(a,start,true),timezone:tz};
 return {day:validDate(a.residency_end_date)?a.residency_end_date:null,unknown:true,reason:'ended',timezone:tz};
}
function whenText(a,r){
 if(!r)return 'Past event';
 if(r.unknown)return r.reason==='ended'?'Series ended'+(r.day?' '+r.day:''):'Schedule needs confirmation';
 const tz=r.timezone||zone(a)||'America/Los_Angeles';
 const dateText=new Intl.DateTimeFormat('en-US',{timeZone:'UTC',weekday:'short',month:'short',day:'numeric',year:'numeric'}).format(new Date(r.day+'T12:00:00Z'));
 if(r.dateOnly)return dateText+' · '+(r.timeUncertain?'Time needs confirmation':'Time to be confirmed');
 const format=new Intl.DateTimeFormat('en-US',{timeZone:tz,hour:'numeric',minute:'2-digit',timeZoneName:'short'});
 return dateText+' · '+format.format(r.start)+(r.end?'–'+format.format(r.end)+(dateKey(wall(r.end,tz))!==r.day?' (next day)':''):' · End time not listed');
}
function stamp(date){return date.toISOString().replace(/[-:]/g,'').replace(/\.\d{3}Z$/,'Z');}
function calendarUrl(a,at=new Date()){
 const r=occurrence(a,at);if(!r||r.unknown||r.dateOnly||!r.start||!r.end)return '';
 const tz=zone(a);if(!tz)return '';
 const recur=norm(a.recurrence),recurring=recur&&recur!=='none';
 const localStamp=d=>stamp(wall(d,tz)).replace(/Z$/,'');
 const params=new URLSearchParams({action:'TEMPLATE',text:a.title||a.name||'Event',dates:recurring?localStamp(r.start)+'/'+localStamp(r.end):stamp(r.start)+'/'+stamp(r.end),details:String(a.description||'').replace(/<[^>]*>/g,''),location:[a.place_name,a.place_street_address,a.place_city,a.place_state,a.place_zip_code].filter(Boolean).join(', '),ctz:tz});
 if(recurring){
  const days=(a.days||a.days_of_week||[]).filter(d=>DAYS.includes(d)).map(d=>CODES[DAYS.indexOf(d)]);
  let rule='RRULE:FREQ='+recur.toUpperCase()+';BYDAY=';
  if(recur==='monthly'){const ord=ordinals(a.monthly_occurrence);if(!ord.length)return '';rule+=ord.flatMap(n=>days.map(d=>n+d)).join(',');}
  else if(recur==='weekly')rule+=days.join(',');else return '';
  if(validDate(a.residency_end_date)){
   const boundary=instant(a.residency_end_date,'23:59',tz);if(!boundary)return '';
   rule+=';UNTIL='+stamp(new Date(+boundary+59000));
  }
  params.set('recur',rule);
 }
 return 'https://calendar.google.com/calendar/render?'+params.toString();
}
function miles(a,b){const rad=x=>x*Math.PI/180;const p=rad(b.lat-a.lat),q=rad(b.lon-a.lon);const h=Math.sin(p/2)**2+Math.cos(rad(a.lat))*Math.cos(rad(b.lat))*Math.sin(q/2)**2;return 3958.7613*2*Math.atan2(Math.sqrt(h),Math.sqrt(Math.max(0,1-h)));}
function select(events,state={},centers={},at=new Date()){
 let missing=0;const rows=events.flatMap(a=>{
  if(state.type&&!(a.event_types||[]).some(t=>norm(t)===norm(state.type)))return [];
  if(state.city&&norm(a.place_city)!==norm(state.city))return [];
  if(state.q&&!norm([a.title,a.brandName,a.place_name,a.place_city,a.place_zip_code].join(' ')).includes(norm(state.q)))return [];
  let distance=null;
  if(state.origin){const c=centers[zip(a.place_zip_code)];if(!c){missing++;return [];}distance=miles(state.origin,c);if(distance>Number(state.radius||25))return [];}
  else if(state.zip&&zip(a.place_zip_code)!==state.zip)return [];
  const r=occurrence(a,at,DAYS.includes(state.day)?state.day:'');if(!r)return [];
  if(['today','week'].includes(state.day)){
   if(r.unknown)return [];
   const today=dateKey(wall(at,r.timezone));const limit=shift(today,state.day==='today'?1:7);
   if(r.day>=limit||(r.dateOnly&&r.day<today))return [];
  }
  return [{a,...r,distance,live:!!(r.start&&r.end&&+r.start<=+at&&+r.end>+at)}];
 });
 const sortValue=r=>r.start?+r.start:validDate(r.day)?+new Date(r.day+'T00:00:00Z'):Infinity;
 rows.sort((a,b)=>(a.unknown?1:0)-(b.unknown?1:0)||(state.sort==='distance'&&a.distance!==null&&b.distance!==null?a.distance-b.distance:0)||sortValue(a)-sortValue(b)||String(a.a.title||'').localeCompare(String(b.a.title||'')));
 return {rows,missing};
}
const loads=new Map();
function loadIndex(url){if(!loads.has(url))loads.set(url,fetch(url,{credentials:'omit',signal:AbortSignal.timeout(15000)}).then(r=>{if(!r.ok)throw Error('Directory unavailable');return r.json();}).catch(e=>{loads.delete(url);throw e;}));return loads.get(url);}
const api={version:VERSION,DAYS,norm,zip,zone,wall,validDate,validTime,instant,permitted,ordinals,flatten,occurrence,isPast,pastOccurrence,whenText,calendarUrl,miles,select,loadIndex};
root.MintyEventCore=api;if(typeof module==='object'&&module.exports)module.exports=api;
})(typeof globalThis==='object'?globalThis:this);
